import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import PendingIcon from '@mui/icons-material/Pending';
import {
  alertColors,
  mainColors,
  secondaryColors
} from '../../theme/themeConfig';
import blueBerryLogoMini from '../../assets/images/bluberry_logo_mini.png';
import blueBerryLetters from '../../assets/images/bluberry.png';

const DRAWER_WIDTH = 240;

const SidebarDrawer = styled(Drawer)(() => ({
  '& .MuiDrawer-paper': {
    background: '#fff',
    borderRight: `1px solid ${secondaryColors.SECONDARY_6}`,
    width: DRAWER_WIDTH
  },
  flexShrink: 0,
  width: DRAWER_WIDTH
}));

const NavButton = styled(ListItemButton)(() => ({
  '&.Mui-selected': {
    background: mainColors.MAIN_LIGHT,
    borderRight: `3px solid ${mainColors.MAIN}`,
    color: mainColors.MAIN
  },
  '&.Mui-selected .MuiListItemIcon-root': {
    color: mainColors.MAIN
  },
  '&:hover': {
    background: mainColors.MAIN_LIGHT
  },
  borderRadius: 0,
  padding: '10px 20px'
}));

interface Props {
  activeTab: string;
  children: React.ReactNode;
  companyName: string;
  complianceStatus?: 'compliant' | 'pending' | 'rejected' | 'missing' | null;
  onLogout: () => void;
  onTabChange: (tab: string) => void;
  partnerStatus?: string;
}

const getComplianceIcon = (
  status?: 'compliant' | 'pending' | 'rejected' | 'missing' | null
) => {
  if (status === 'compliant')
    return <CheckCircleIcon sx={{ color: 'success.main' }} />;
  if (status === 'rejected')
    return <ErrorOutlineIcon sx={{ color: 'error.main' }} />;
  if (status === 'pending')
    return <PendingIcon sx={{ color: 'warning.main' }} />;
  if (status === 'missing')
    return <ErrorOutlineIcon sx={{ color: 'error.main' }} />;
  return <CheckCircleIcon />;
};

const PortalLayout: React.FC<Props> = function PortalLayout({
  activeTab,
  children,
  companyName,
  complianceStatus,
  onLogout,
  onTabChange,
  partnerStatus
}) {
  const navItems = [
    {
      icon: <PersonOutlineIcon />,
      key: 'profile',
      label: 'Profile'
    },
    {
      icon: <WorkOutlineIcon />,
      key: 'projects',
      label: 'Projects'
    },
    {
      icon: <DescriptionOutlinedIcon />,
      key: 'documents',
      label: 'Documents'
    },
    {
      icon: getComplianceIcon(complianceStatus),
      key: 'compliance',
      label: 'Compliance'
    }
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <SidebarDrawer variant="permanent">
        <Box sx={{ p: '24px 20px 16px' }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              mb: 1
            }}
          >
            <img
              alt="Bluberry"
              src={blueBerryLogoMini}
              style={{ height: 28 }}
            />
            <img alt="Bluberry" src={blueBerryLetters} style={{ height: 16 }} />
          </Box>
          <Typography
            sx={{
              color: secondaryColors.SECONDARY,
              fontSize: '11px',
              letterSpacing: '.5px',
              textTransform: 'uppercase'
            }}
          >
            Partner portal
          </Typography>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mt: 0.5 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              {companyName}
            </Typography>
            {partnerStatus && partnerStatus !== 'active' && (
              <Box
                sx={{
                  alignItems: 'center',
                  background:
                    partnerStatus === 'suspended'
                      ? alertColors.ALERT_6
                      : secondaryColors.SECONDARY_6,
                  borderRadius: '12px',
                  display: 'inline-flex',
                  fontSize: '10px',
                  fontWeight: 600,
                  gap: '4px',
                  px: '7px',
                  py: '2px'
                }}
              >
                <Box
                  sx={{
                    background:
                      partnerStatus === 'suspended'
                        ? alertColors.ALERT
                        : secondaryColors.SECONDARY_1,
                    borderRadius: '50%',
                    height: 5,
                    width: 5
                  }}
                />
                <span
                  style={{
                    color:
                      partnerStatus === 'suspended'
                        ? alertColors.ALERT
                        : secondaryColors.SECONDARY
                  }}
                >
                  {partnerStatus.replace(/_/g, ' ')}
                </span>
              </Box>
            )}
          </Box>
        </Box>

        <List sx={{ flex: 1, pt: 1 }}>
          {navItems.map((item) => (
            <NavButton
              key={item.key}
              onClick={() => onTabChange(item.key)}
              selected={activeTab === item.key}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '13px',
                  fontWeight: activeTab === item.key ? 600 : 400
                }}
              />
            </NavButton>
          ))}
        </List>

        <List sx={{ pb: 2 }}>
          <NavButton onClick={onLogout}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LogoutIcon sx={{ color: secondaryColors.SECONDARY }} />
            </ListItemIcon>
            <ListItemText
              primary="Sign out"
              primaryTypographyProps={{
                color: secondaryColors.SECONDARY,
                fontSize: '13px'
              }}
            />
          </NavButton>
        </List>
      </SidebarDrawer>

      <Box
        component="main"
        sx={{
          background: '#F4F7FB',
          flex: 1,
          p: '24px'
        }}
      >
        {partnerStatus === 'suspended' && (
          <Box
            sx={{
              alignItems: 'center',
              background: alertColors.ALERT_6,
              border: `1px solid ${alertColors.ALERT}`,
              borderRadius: '10px',
              display: 'flex',
              gap: 1.5,
              mb: 2,
              p: 2
            }}
          >
            <ErrorOutlineIcon sx={{ color: alertColors.ALERT }} />
            <Box>
              <Typography
                sx={{
                  color: alertColors.ALERT,
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                Partnership suspended
              </Typography>
              <Typography
                sx={{
                  color: secondaryColors.SECONDARY,
                  fontSize: '12px',
                  mt: 0.25
                }}
              >
                Your partnership has been suspended by the host company. Project
                assignments and new work have been paused. Please contact your
                host company for more information.
              </Typography>
            </Box>
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

PortalLayout.defaultProps = {
  complianceStatus: null,
  partnerStatus: undefined
};

export default PortalLayout;
